import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Pencil,
  Trash2,
  Download,
  LogOut,
  Loader2,
  Briefcase,
  Users,
  Inbox,
  ExternalLink,
} from "lucide-react";
import { api, getToken, setToken, API } from "@/lib/api";

const ROLE_TYPES = ["CEO", "COO", "Commercial Director", "CCO", "MD", "VP Growth"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("jobs");
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setMe(res.data))
      .catch(() => {
        setToken(null);
        navigate("/admin/login");
      });
  }, [navigate]);

  const logout = () => {
    setToken(null);
    navigate("/admin/login");
  };

  if (!me) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0E2750] text-slate-400">
        <Loader2 className="animate-spin mr-3" size={20} />
        Loading admin…
      </div>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#0E2750] text-white">
      <header className="sticky top-0 z-30 backdrop-blur-2xl bg-black/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="font-display text-lg tracking-tight">
            Arc<span className="text-[#C9A876]">point</span>{" "}
            <span className="text-xs uppercase tracking-[0.25em] text-slate-500 ml-2">Admin</span>
          </Link>
          <div className="flex items-center gap-5">
            <span className="hidden md:inline text-xs text-slate-400">{me.email}</span>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#C9A876]"
            >
              <LogOut size={14} strokeWidth={1.5} /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex gap-1">
          {[
            { id: "jobs", label: "Mandates", icon: Briefcase },
            { id: "candidates", label: "Candidates", icon: Users },
            { id: "contacts", label: "Messages", icon: Inbox },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${
                  active
                    ? "border-[#C9A876] text-white"
                    : "border-transparent text-slate-400 hover:text-white"
                }`}
              >
                <Icon size={14} strokeWidth={1.5} />
                {t.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14">
        {tab === "jobs" && <JobsPanel />}
        {tab === "candidates" && <CandidatesPanel />}
        {tab === "contacts" && <ContactsPanel />}
      </div>
    </main>
  );
}

/* ---------- Jobs ---------- */

function JobsPanel() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/jobs", { params: { active_only: false } });
      setJobs(data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const remove = async (id) => {
    if (!window.confirm("Delete this mandate?")) return;
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Mandate deleted");
      refresh();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-display text-2xl md:text-3xl tracking-tight">Mandates</h2>
          <p className="text-sm text-slate-400 mt-1">Manage live leadership opportunities.</p>
        </div>
        <button
          data-testid="admin-create-job"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#C9A876] hover:bg-[#DCC298] text-black text-sm font-semibold"
        >
          <Plus size={14} strokeWidth={2} /> New mandate
        </button>
      </div>

      {loading ? (
        <Loading />
      ) : jobs.length === 0 ? (
        <Empty label="No mandates yet." />
      ) : (
        <div className="border border-white/10 divide-y divide-white/10">
          {jobs.map((j) => (
            <div
              key={j.id}
              data-testid={`admin-job-row-${j.id}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-4 px-5 py-5 hover:bg-white/[0.02]"
            >
              <div className="md:col-span-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[#C9A876] mb-1">
                  {j.role_type}
                </div>
                <div className="font-display text-lg text-white">{j.title}</div>
                <div className="text-xs text-slate-400 mt-1">
                  {j.location} · {j.salary}
                </div>
              </div>
              <div className="md:col-span-5 text-sm text-slate-400 line-clamp-2">
                {j.description}
              </div>
              <div className="md:col-span-2 flex md:justify-end items-center gap-2">
                <span
                  className={`text-xs uppercase tracking-[0.2em] px-2 py-1 border ${
                    j.is_active
                      ? "border-[#C9A876]/40 text-[#C9A876]"
                      : "border-white/15 text-slate-500"
                  }`}
                >
                  {j.is_active ? "Live" : "Hidden"}
                </span>
                <button
                  data-testid={`admin-edit-job-${j.id}`}
                  onClick={() => setEditing(j)}
                  className="p-2 text-slate-400 hover:text-[#C9A876]"
                  aria-label="Edit"
                >
                  <Pencil size={16} strokeWidth={1.5} />
                </button>
                <button
                  data-testid={`admin-delete-job-${j.id}`}
                  onClick={() => remove(j.id)}
                  className="p-2 text-slate-400 hover:text-red-400"
                  aria-label="Delete"
                >
                  <Trash2 size={16} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {(creating || editing) && (
          <JobFormModal
            initial={editing}
            onClose={() => {
              setCreating(false);
              setEditing(null);
            }}
            onSaved={() => {
              setCreating(false);
              setEditing(null);
              refresh();
            }}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

function JobFormModal({ initial, onClose, onSaved }) {
  const editing = Boolean(initial);
  const [form, setForm] = useState(
    initial || {
      title: "",
      role_type: "CEO",
      location: "",
      salary: "",
      description: "",
      is_active: true,
    }
  );
  const [saving, setSaving] = useState(false);
  const change = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/jobs/${initial.id}`, form);
        toast.success("Mandate updated");
      } else {
        await api.post("/jobs", form);
        toast.success("Mandate created");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full bg-white/5 border border-white/10 text-white placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-[#C9A876] focus:ring-1 focus:ring-[#C9A876]";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-job-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-[#15315F] border border-[#C9A876]/30 p-8 max-h-[90vh] overflow-y-auto"
      >
        <h3 className="font-display text-2xl tracking-tight mb-6">
          {editing ? "Edit mandate" : "New mandate"}
        </h3>
        <form onSubmit={save} className="space-y-4">
          <label className="block">
            <span className="font-mono-eyebrow text-slate-400 mb-2 block">Title</span>
            <input data-testid="admin-job-title" required className={inputCls} value={form.title} onChange={change("title")} />
          </label>
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="font-mono-eyebrow text-slate-400 mb-2 block">Role type</span>
              <select
                data-testid="admin-job-role-type"
                className={inputCls}
                value={form.role_type}
                onChange={change("role_type")}
              >
                {ROLE_TYPES.map((r) => (
                  <option key={r} value={r} className="bg-[#15315F]">
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="font-mono-eyebrow text-slate-400 mb-2 block">Location</span>
              <input data-testid="admin-job-location" required className={inputCls} value={form.location} onChange={change("location")} />
            </label>
          </div>
          <label className="block">
            <span className="font-mono-eyebrow text-slate-400 mb-2 block">Salary</span>
            <input data-testid="admin-job-salary" required className={inputCls} value={form.salary} onChange={change("salary")} />
          </label>
          <label className="block">
            <span className="font-mono-eyebrow text-slate-400 mb-2 block">Description</span>
            <textarea data-testid="admin-job-description" required rows={5} className={`${inputCls} resize-none`} value={form.description} onChange={change("description")} />
          </label>
          <label className="flex items-center gap-3 text-sm text-slate-300">
            <input data-testid="admin-job-active" type="checkbox" checked={form.is_active} onChange={change("is_active")} className="accent-[#C9A876] w-4 h-4" />
            Show publicly (live mandate)
          </label>
          <div className="flex justify-end gap-3 pt-3">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-white/15 text-slate-300 text-sm">
              Cancel
            </button>
            <button
              type="submit"
              data-testid="admin-job-save"
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C9A876] hover:bg-[#DCC298] text-black text-sm font-semibold disabled:opacity-60"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {editing ? "Save changes" : "Create mandate"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

/* ---------- Candidates ---------- */

function CandidatesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/candidates")
      .then((res) => setItems(res.data || []))
      .finally(() => setLoading(false));
  }, []);

  const downloadCv = async (fileId, filename) => {
    try {
      const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "cv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <section>
      <div className="mb-8">
        <h2 className="font-display text-2xl md:text-3xl tracking-tight">Candidates</h2>
        <p className="text-sm text-slate-400 mt-1">
          Confidential applications submitted via the website.
        </p>
      </div>
      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <Empty label="No candidate submissions yet." />
      ) : (
        <div className="border border-white/10 divide-y divide-white/10">
          {items.map((c) => (
            <div
              key={c.id}
              data-testid={`admin-candidate-${c.id}`}
              className="px-5 py-5 grid grid-cols-1 md:grid-cols-12 gap-4 hover:bg-white/[0.02]"
            >
              <div className="md:col-span-3">
                <div className="font-display text-base text-white">{c.full_name}</div>
                <div className="text-xs text-slate-400">{c.email}</div>
                {c.phone && <div className="text-xs text-slate-500">{c.phone}</div>}
              </div>
              <div className="md:col-span-3 text-sm text-slate-400 space-y-1">
                <div>
                  <span className="text-slate-500">Salary: </span>
                  {c.salary_expectation}
                </div>
                <div>
                  <span className="text-slate-500">Notice: </span>
                  {c.notice_period}
                </div>
                {c.current_role && (
                  <div>
                    <span className="text-slate-500">Role: </span>
                    {c.current_role}
                  </div>
                )}
              </div>
              <div className="md:col-span-3 text-sm text-slate-400">
                {c.job_title ? (
                  <div className="text-[#C9A876] text-xs uppercase tracking-[0.2em] mb-1">
                    Applied: {c.job_title}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 uppercase tracking-[0.2em] mb-1">
                    Network application
                  </div>
                )}
                {c.cv_summary && (
                  <div
                    data-testid={`admin-cv-summary-${c.id}`}
                    className="mt-2 mb-2 border-l-2 border-[#C9A876]/60 pl-3 py-1 text-slate-300 leading-relaxed"
                  >
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A876] mb-1">
                      AI CV summary
                    </div>
                    <div className="whitespace-pre-wrap">{c.cv_summary}</div>
                  </div>
                )}
                {c.message && <div className="line-clamp-3">{c.message}</div>}
              </div>
              <div className="md:col-span-3 flex flex-wrap gap-2 md:justify-end items-start">
                <a
                  href={c.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-slate-300 border border-white/15 hover:border-[#C9A876]/50 px-3 py-1.5"
                >
                  LinkedIn <ExternalLink size={12} strokeWidth={1.5} />
                </a>
                {c.cv_file_id && (
                  <button
                    data-testid={`admin-cv-download-${c.id}`}
                    onClick={() => downloadCv(c.cv_file_id, c.cv_filename)}
                    className="inline-flex items-center gap-1 text-xs text-black bg-[#C9A876] hover:bg-[#DCC298] px-3 py-1.5 font-semibold"
                  >
                    <Download size={12} strokeWidth={2} /> CV
                  </button>
                )}
                <div className="basis-full text-[10px] text-slate-500 uppercase tracking-[0.2em] mt-1">
                  {new Date(c.created_at).toLocaleString("en-GB")}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------- Contacts ---------- */

function ContactsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/contact")
      .then((res) => setItems(res.data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section>
      <div className="mb-8">
        <h2 className="font-display text-2xl md:text-3xl tracking-tight">Messages</h2>
        <p className="text-sm text-slate-400 mt-1">
          Founder-direct enquiries from the contact form.
        </p>
      </div>
      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <Empty label="No messages yet." />
      ) : (
        <div className="border border-white/10 divide-y divide-white/10">
          {items.map((m) => (
            <div
              key={m.id}
              data-testid={`admin-contact-${m.id}`}
              className="px-5 py-5 grid grid-cols-1 md:grid-cols-12 gap-4 hover:bg-white/[0.02]"
            >
              <div className="md:col-span-3">
                <div className="font-display text-base text-white">{m.name}</div>
                <a
                  href={`mailto:${m.email}`}
                  className="text-xs text-[#C9A876] hover:underline"
                >
                  {m.email}
                </a>
                {m.company && <div className="text-xs text-slate-500 mt-1">{m.company}</div>}
              </div>
              <div className="md:col-span-7 text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
                {m.message}
              </div>
              <div className="md:col-span-2 text-[10px] text-slate-500 uppercase tracking-[0.2em] md:text-right">
                {new Date(m.created_at).toLocaleString("en-GB")}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------- shared ---------- */

function Loading() {
  return (
    <div className="flex items-center justify-center py-20 text-slate-400">
      <Loader2 className="animate-spin mr-3" size={18} /> Loading…
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="border border-dashed border-white/10 py-16 text-center text-slate-500 text-sm">
      {label}
    </div>
  );
}
